import { Component, OnInit, Renderer2 } from '@angular/core';
import { ApiService } from '../api.service';
import { ActivatedRoute, Router } from "@angular/router";
import { ToastrService } from 'ngx-toastr';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { convertToNgbDate, datePickerMinDate, noWhitespaceInRequiredValidator, mobilePattern } from '../shared/constants';
import { NgbDateParserFormatter, NgbDateStruct } from '@ng-bootstrap/ng-bootstrap';

export class CustomDateParserFormatter extends NgbDateParserFormatter {
  readonly DELIMITER = '-';

  parse(value: string): NgbDateStruct | null {
    if (value) {
      const delimiter = value.includes('/') ? '/' : this.DELIMITER;
      const parts = value.trim().split(delimiter);
      if (parts.length === 3) {
        return {
          day: parseInt(parts[0], 10),
          month: parseInt(parts[1], 10),
          year: parseInt(parts[2], 10)
        };
      }
    }
    return null;
  }

  format(date: NgbDateStruct | null): string {
    return date ?
      `${date.day < 10 ? '0' + date.day : date.day}${this.DELIMITER}${date.month < 10 ? '0' + date.month : date.month}${this.DELIMITER}${date.year}` :
      '';
  }
}

@Component({
  selector: 'app-user-profile',
  templateUrl: './user-profile.component.html',
  styleUrl: './user-profile.component.css',
  providers: [
    { provide: NgbDateParserFormatter, useClass: CustomDateParserFormatter }
  ]
})
export class UserProfileComponent implements OnInit {
  moduleForm: FormGroup;
  formSubmitted = false;
  showLoader = false;
  minStartDate = datePickerMinDate;
  maxDate = convertToNgbDate(new Date());
  countryCodes: any[] = [];
  showConfirmPopup = false;
  showDonePopup = false;

  constructor(private apiService: ApiService, private router: Router, private renderer: Renderer2, private toastr: ToastrService, private route: ActivatedRoute, private formBuilder: FormBuilder) {
    this.moduleForm = this.formBuilder.group({
      fullName: [null, [Validators.required, noWhitespaceInRequiredValidator]],
      dob: [null, [Validators.required, noWhitespaceInRequiredValidator]],
      imoNumber: [null, [Validators.required, noWhitespaceInRequiredValidator, Validators.pattern('^[0-9]{7}$')]],
      passportNumber: [null, [Validators.required, noWhitespaceInRequiredValidator, Validators.pattern('^[A-Za-z0-9]{6,12}$')]],
      countryCode: [null, [Validators.required, noWhitespaceInRequiredValidator]],
      mobileNumber: [null, [Validators.required, noWhitespaceInRequiredValidator, Validators.pattern(mobilePattern)]]
    });
  }

  ngOnInit(): void {
    this.minStartDate = convertToNgbDate(new Date());
    this.getCountryCodes();
  }

  getCountryCodes() {
    this.apiService.countryCodes().subscribe({
      next: (data: any) => {
        data.map((c: any) => {
          c.display = c.dial_code + ' (' + c.name + ')';
          return c;
        });
        this.countryCodes = data;
      }, error: (error) => {
        this.countryCodes = [];
      }
    });
  }

  get f() { return this.moduleForm.controls; }

  showError(field: string) {
    if (this.f[field].invalid && (this.formSubmitted || this.f[field].touched || this.f[field].dirty)) {
      return true;
    }
    return false;
  }

  getFirstValidationError(): string {
    const validationMessages: any = {
      fullName: {
        required: 'Full Name is required',
        minlength: 'Full Name must be at least 3 characters'
      },
      dob: {
        required: 'Date of Birth is required'
      },
      imoNumber: {
        required: 'IMO Number is required',
        pattern: 'IMO Number must contain 7 digits'
      },
      passportNumber: {
        required: 'Passport Number is required',
        pattern: 'Invalid Passport Number'
      },
      countryCode: {
        required: 'Country Code is required'
      },
      mobileNumber: {
        required: 'Mobile Number is required',
        pattern: 'Invalid Mobile Number'
      }
    };

    for (const field in this.moduleForm.controls) {
      const control = this.moduleForm.get(field);

      if (control && control.errors) {
        const firstErrorKey = Object.keys(control.errors)[0];
        return validationMessages[field]?.[firstErrorKey] || `${field} is invalid`;
      }
    }

    return 'Please check all required fields.';
  }

  onSubmit() {
    if (this.moduleForm.invalid) {

      const firstError = this.getFirstValidationError();

      this.toastr.warning(firstError, 'Validation Error', {
        timeOut: 3000,
        tapToDismiss: true,
        closeButton: true
      });

      this.moduleForm.markAllAsTouched();
      return;
    }

    this.showConfirmPopup = true;
  }

  confirmSave() {
    this.showConfirmPopup = false;
    this.showLoader = true;
    const formValue = this.moduleForm.value;
    const payload = {
      country_code: formValue.countryCode,
      mobile_no: formValue.mobileNumber,
      name: formValue.fullName,
      dob: formValue.dob ? this.apiService.getDateStringFromDate(formValue.dob) : null,
      passport_no: formValue.passportNumber,
      imo_no: formValue.imoNumber
    };
    this.apiService.deleteUserProfile(payload).subscribe({
      next: (data: any) => {
        this.showLoader = false;
        this.showDonePopup = true;

        setTimeout(() => {
          this.showDonePopup = false;
          this.apiService.navigateToLogin(false);
        }, 5000);
      },
      error: () => {
        this.showLoader = false;
        this.toastr.error("An error occurred while saving the profile.", "Error", {
          timeOut: 3000,
          tapToDismiss: true,
          closeButton: true
        });
      }
    });
  }

  closeConfirm() {
    this.showConfirmPopup = false;
  }

}